/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type ReactElement } from 'react';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export interface CartLineItemIncludesPart {
    label: string;
    quantity?: number | string;
}

interface CartLineItemProductIncludesCardProps {
    title?: string;
    items: CartLineItemIncludesPart[];
    className?: string;
}

export default function CartLineItemProductIncludesCard({
    title,
    items,
    className,
}: CartLineItemProductIncludesCardProps): ReactElement | null {
    if (!items.length) {
        return null;
    }

    return (
        <div className={cn('hidden w-full overflow-hidden rounded border border-border bg-background', className)}>
            <div className="bg-muted px-3 py-2">
                <p className="text-sm font-semibold leading-5 text-card-foreground">{title ?? 'Product Includes'}</p>
            </div>
            <div className="px-3 py-2">
                {items.map((item, index) => {
                    const quantityText =
                        typeof item.quantity === 'number'
                            ? `x${item.quantity}`
                            : item.quantity
                              ? String(item.quantity)
                              : '';

                    // Bundle components use `label` as the stable identity — a bundle
                    // is a set of distinct SKUs with the multiplicity captured in
                    // `quantity`, so two entries never share the same label.
                    return (
                        <div key={item.label}>
                            <div className="flex items-start justify-between gap-2 text-xs leading-4 text-muted-foreground">
                                <span className="min-w-0 break-words">{item.label}</span>
                                {quantityText ? <span className="shrink-0">{quantityText}</span> : null}
                            </div>
                            {index < items.length - 1 ? <Separator className="my-1.5 bg-border" /> : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
